'use client'

// The segmented control (Brand Integrity's status switch, a list's status
// filter): mono 10 px segments in a hairline box, the active one on the
// primary colour. `multi` lets several be active (a filter), with none
// active meaning everything.

export type Segment<T extends string> = { value: T; label: React.ReactNode; count?: number; title?: string }

export function Segmented<T extends string>({ segments, value, onChange, multi = false, size = 'md', className = '' }: {
  segments: Segment<T>[]
  value: T | T[] | null
  onChange: (next: T) => void
  multi?: boolean
  size?: 'sm' | 'md'
  className?: string
}) {
  const active = new Set(Array.isArray(value) ? value : value ? [value] : [])
  return (
    <div role={multi ? 'group' : 'radiogroup'} className={`inline-flex gap-1 border border-border bg-card p-1 ${className}`}>
      {segments.map((s) => {
        const on = active.has(s.value)
        return (
          <button
            key={s.value}
            type="button"
            role={multi ? 'button' : 'radio'}
            aria-checked={multi ? undefined : on}
            aria-pressed={multi ? on : undefined}
            title={s.title}
            onClick={() => onChange(s.value)}
            className={`rounded-[2px] font-mono uppercase tracking-widest transition-colors ${size === 'sm' ? 'px-2.5 py-1 text-micro' : 'px-3.5 py-1.5 text-label'} ${
              on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {s.label}
            {s.count != null && <span className={`ml-1.5 ${on ? 'opacity-70' : 'text-subtle'}`}>{s.count}</span>}
          </button>
        )
      })}
    </div>
  )
}
